package co.maytes.standupbot.dashboard.features.settings.sections

import co.maytes.standupbot.dashboard.components.motion.StateSwap
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import co.maytes.standupbot.dashboard.components.feedback.ConfirmModal
import co.maytes.standupbot.dashboard.components.feedback.ConfirmTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.features.membersSettings.MembersSettingsContainer
import co.maytes.standupbot.dashboard.features.membersSettings.MembersSettingsContract
import co.maytes.standupbot.dashboard.features.settings.InviteMemberModal
import co.maytes.standupbot.dashboard.features.settings.LinkGithubIdentityModal
import co.maytes.standupbot.dashboard.features.settings.LinkLinearIdentityModal
import co.maytes.standupbot.dashboard.features.settings.MemberAction
import co.maytes.standupbot.dashboard.features.settings.MemberDialogCopy
import co.maytes.standupbot.dashboard.features.settings.MemberRow
import co.maytes.standupbot.dashboard.features.settings.MembersCopy
import co.maytes.standupbot.dashboard.features.settings.MembersSkeletonRows
import co.maytes.standupbot.dashboard.features.settings.MembersTableHeader
import co.maytes.standupbot.dashboard.features.settings.MembersWideSection
import co.maytes.standupbot.dashboard.features.settings.Plain
import co.maytes.standupbot.dashboard.features.settings.SettingsBars
import co.maytes.standupbot.dashboard.features.settings.phase
import co.maytes.standupbot.dashboard.features.settings.SettingsEmpty
import co.maytes.standupbot.dashboard.features.settings.SettingsError
import co.maytes.standupbot.dashboard.features.settings.SettingsGroup
import co.maytes.standupbot.dashboard.features.settings.SettingsPage
import co.maytes.standupbot.dashboard.features.settings.SettingsPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsRowItem
import co.maytes.standupbot.dashboard.features.settings.SettingsSectionRow
import co.maytes.standupbot.dashboard.features.settings.SettingsSubtitle
import co.maytes.standupbot.dashboard.features.settings.Strong
import co.maytes.standupbot.dashboard.features.settings.TransferOwnershipModal
import co.maytes.standupbot.dashboard.features.settings.availableMemberActions
import co.maytes.standupbot.dashboard.features.settings.copyTextToClipboard
import co.maytes.standupbot.dashboard.features.settings.rememberSettingsStore
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.workspace.InviteLinkDto
import co.maytes.standupbot.shared.policy.MemberPermissions
import com.kttipay.mvi.compose.subscribe
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Clock
import org.jetbrains.compose.web.attributes.InputType

private const val SEARCH_MAX_WIDTH = "20rem"

@Composable
fun MembersSection() {
    val auth = rememberAuthStore()
    val settingsStore = rememberSettingsStore()
    val ui = rememberUiStore()
    val scope = rememberCoroutineScope()

    val workspaceId = auth.currentUser?.workspace?.id
    val actorRole = auth.currentUser?.role
    val canManage = actorRole in setOf(RoleDto.OWNER, RoleDto.ADMIN)

    val container = remember(settingsStore) { MembersSettingsContainer(scope, settingsStore) }
    val state by container.subscribe()

    LaunchedEffect(workspaceId, canManage) {
        if (!workspaceId.isNullOrBlank()) {
            container.intent(MembersSettingsContract.Intent.Load(workspaceId, canManage))
        }
    }

    val copyWithToast: (String) -> Unit = { value ->
        val copied = copyTextToClipboard(value)
        ui.showToast(
            if (copied) "Link copied to clipboard" else "Copy is not supported in this browser",
            if (copied) ToastKind.Success else ToastKind.Error,
        )
    }

    LaunchedEffect(container) {
        container.store.actions.collect { action ->
            when (action) {
                is MembersSettingsContract.Action.Notify ->
                    ui.showToast(action.message, if (action.success) ToastKind.Success else ToastKind.Error)
                MembersSettingsContract.Action.OwnershipTransferred -> auth.bootstrap()
            }
        }
    }

    MembersView(state, actorRole, onCopyLink = copyWithToast) { container.intent(it) }

    if (state.inviteOpen && actorRole != null) {
        InviteMemberModal(
            state = state,
            actorRole = actorRole,
            dispatch = { container.intent(it) },
            onCopy = copyWithToast,
        )
    }

    state.pendingInviteRevocation?.let { invite ->
        ConfirmModal(
            title = "Revoke invite for ${invite.displayName}?",
            body = "The link stops working immediately. You can send a new invite later.",
            confirmLabel = "Revoke",
            cancelLabel = "Keep",
            tone = ConfirmTone.Danger,
            onDismiss = { container.intent(MembersSettingsContract.Intent.CancelInviteRevocation) },
            onConfirm = { container.intent(MembersSettingsContract.Intent.ConfirmInviteRevocation) },
        )
    }

    state.pendingDisable?.let { member ->
        ConfirmModal(
            title = "Disable ${member.displayName}?",
            body = MemberDialogCopy.disable(member),
            confirmLabel = "Disable",
            cancelLabel = "Cancel",
            tone = ConfirmTone.Danger,
            onDismiss = { container.intent(MembersSettingsContract.Intent.CancelDisable) },
            onConfirm = { container.intent(MembersSettingsContract.Intent.ConfirmDisable) },
        )
    }

    state.pendingRestore?.let { member ->
        ConfirmModal(
            title = "Restore ${member.displayName}?",
            body = MemberDialogCopy.restore(member),
            confirmLabel = "Restore",
            onDismiss = { container.intent(MembersSettingsContract.Intent.CancelRestore) },
            onConfirm = { container.intent(MembersSettingsContract.Intent.ConfirmRestore) },
        )
    }

    state.pendingErase?.let { member ->
        ConfirmModal(
            title = "Erase ${member.displayName}'s personal data?",
            body = MemberDialogCopy.erase(member),
            confirmLabel = "Erase personal data",
            tone = ConfirmTone.Danger,
            confirmMatch = member.displayName,
            onDismiss = { container.intent(MembersSettingsContract.Intent.CancelErase) },
            onConfirm = { container.intent(MembersSettingsContract.Intent.ConfirmErase) },
        )
    }

    state.transferTo?.let { member ->
        TransferOwnershipModal(
            member = member,
            workspaceName = auth.currentUser?.workspace?.name.orEmpty(),
            submitting = state.transferSubmitting,
            error = state.transferError,
            needsSignIn = state.transferNeedsSignIn,
            onConfirm = { typed -> container.intent(MembersSettingsContract.Intent.ConfirmTransfer(typed)) },
            onDismiss = { container.intent(MembersSettingsContract.Intent.CancelTransfer) },
        )
    }

    state.linkLinearFor?.let { member ->
        LinkLinearIdentityModal(
            displayName = member.displayName,
            currentLinearUserId = member.linearUserId,
            hintEmail = member.email,
            onSave = { id ->
                container.intent(MembersSettingsContract.Intent.SubmitLinkLinear(id))
            },
            onDismiss = { container.intent(MembersSettingsContract.Intent.CloseLinkLinear) },
        )
    }

    state.linkGithubFor?.let { member ->
        LinkGithubIdentityModal(
            displayName = member.displayName,
            currentGithubLogin = member.githubLogin,
            onSave = { login ->
                container.intent(MembersSettingsContract.Intent.SubmitLinkGithub(login))
            },
            onDismiss = { container.intent(MembersSettingsContract.Intent.CloseLinkGithub) },
        )
    }
}

@Composable
private fun MembersView(
    state: MembersSettingsContract.State,
    actorRole: RoleDto?,
    onCopyLink: (String) -> Unit,
    dispatch: (MembersSettingsContract.Intent) -> Unit,
) {
    val ready = state.status == MembersSettingsContract.Status.Ready
    SettingsPage(
        group = SettingsGroup.Workspace,
        title = "Members",
        subtitle = if (ready) {
            {
                val copy = MembersCopy.subtitle(state.members, Clock.System.now())
                SettingsSubtitle {
                    Strong(copy.strong)
                    Plain(copy.rest)
                }
            }
        } else {
            null
        },
        action = {
            Button(
                label = "Invite teammates",
                variant = ButtonVariant.Primary,
                enabled = state.canManage,
                onClick = { dispatch(MembersSettingsContract.Intent.OpenInvite) },
            )
        },
    ) {
        PeopleSection(state = state, actorRole = actorRole, onCopyLink = onCopyLink, dispatch = dispatch)
        InviteLinksSection(state = state, onCopyLink = onCopyLink, dispatch = dispatch)
    }
}

@Composable
private fun PeopleSection(
    state: MembersSettingsContract.State,
    actorRole: RoleDto?,
    onCopyLink: (String) -> Unit,
    dispatch: (MembersSettingsContract.Intent) -> Unit,
) {
    MembersWideSection(anchor = "people", title = "People", description = MembersCopy.PEOPLE_DESCRIPTION) {
        StateSwap(state = state.status.phase()) {
            when (state.status) {
                MembersSettingsContract.Status.Idle, MembersSettingsContract.Status.Loading -> {
                    MembersTableHeader()
                    MembersSkeletonRows()
                }
                is MembersSettingsContract.Status.Error ->
                    SettingsError(what = "the members") {
                        state.workspaceId.takeIf { it.isNotBlank() }?.let {
                            dispatch(MembersSettingsContract.Intent.Load(it, state.canManage))
                        }
                    }
                MembersSettingsContract.Status.Ready -> {
                    MembersToolbar(state = state, dispatch = dispatch)
                    state.actionError?.let { message ->
                        SpanText(
                            text = message,
                            modifier = Modifier
                                .fontSize(Typography.bodySmall.size)
                                .color(Palette.status(StatusHue.Danger).solid)
                                .margin(bottom = Spacing.sm),
                        )
                    }
                    MembersTable(state = state, actorRole = actorRole, onCopyLink = onCopyLink, dispatch = dispatch)
                }
            }
        }
    }
}

@Composable
private fun MembersToolbar(
    state: MembersSettingsContract.State,
    dispatch: (MembersSettingsContract.Intent) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = Spacing.sm)
            .gap(Spacing.sm),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TextInput(
            value = state.search,
            onValueChange = { dispatch(MembersSettingsContract.Intent.SetSearch(it)) },
            placeholder = "Search by name or email",
            ariaLabel = "Search members",
            type = InputType.Search,
            modifier = Modifier.fillMaxWidth().styleModifier { property("max-width", SEARCH_MAX_WIDTH) },
        )
        if (state.disabledCount > 0) {
            Button(
                label = if (state.showDisabled) "Hide disabled (${state.disabledCount})" else "Show disabled (${state.disabledCount})",
                variant = ButtonVariant.Ghost,
                size = ButtonSize.Sm,
                onClick = { dispatch(MembersSettingsContract.Intent.SetShowDisabled(!state.showDisabled)) },
            )
        }
    }
}

@Composable
private fun MembersTable(
    state: MembersSettingsContract.State,
    actorRole: RoleDto?,
    onCopyLink: (String) -> Unit,
    dispatch: (MembersSettingsContract.Intent) -> Unit,
) {
    val filtered = state.filtered
    val showEmail = actorRole != null && MemberPermissions.canSeeEmails(actorRole)
    Column(modifier = Modifier.fillMaxWidth()) {
        MembersTableHeader()
        filtered.forEach { member ->
            MemberRow(
                member = member,
                canManage = state.canManage && !member.isCurrentUser,
                showEmail = showEmail,
                lastOwner = member.role == RoleDto.OWNER && state.ownerCount <= 1,
                actions = availableMemberActions(actorRole, member, hasFreshInviteLink = member.userId in state.freshInviteUrls),
                onAction = { action ->
                    if (action == MemberAction.CopyInviteLink) {
                        state.freshInviteUrls[member.userId]?.let(onCopyLink)
                    } else {
                        dispatch(MembersSettingsContract.Intent.OnMemberAction(member, action))
                    }
                },
            )
        }
    }
    if (filtered.isEmpty()) {
        SettingsEmpty(text = if (state.search.isBlank()) MembersCopy.NO_MEMBERS else MembersCopy.NO_MATCH)
    } else if (state.search.isBlank() && state.members.size <= 1) {
        Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.md)) { SettingsEmpty(text = MembersCopy.ONLY_YOU) }
    }
}

@Composable
private fun InviteLinksSection(
    state: MembersSettingsContract.State,
    onCopyLink: (String) -> Unit,
    dispatch: (MembersSettingsContract.Intent) -> Unit,
) {
    SettingsSectionRow(anchor = "invite-links", title = "Invite links", description = MembersCopy.LINKS_DESCRIPTION) {
        SettingsPanel {
            when {
                !state.canManage -> SettingsEmpty(text = MembersCopy.LINKS_ADMINS_ONLY)
                state.inviteLinksFailed ->
                    SettingsError(what = "the invite links") { dispatch(MembersSettingsContract.Intent.LoadInviteLinks) }
                !state.inviteLinksLoaded -> SettingsBars(rows = 2)
                state.inviteLinks.isEmpty() ->
                    SettingsEmpty(text = MembersCopy.NO_LINKS) {
                        Button(
                            label = "Create link",
                            variant = ButtonVariant.Secondary,
                            size = ButtonSize.Sm,
                            onClick = { dispatch(MembersSettingsContract.Intent.OpenInviteLinks) },
                        )
                    }
                else -> InviteLinkRows(links = state.inviteLinks, onCopyLink = onCopyLink, dispatch = dispatch)
            }
        }
    }
}

@Composable
private fun InviteLinkRows(
    links: List<InviteLinkDto>,
    onCopyLink: (String) -> Unit,
    dispatch: (MembersSettingsContract.Intent) -> Unit,
) {
    val now = remember(links) { Clock.System.now() }
    links.forEachIndexed { index, link ->
        SettingsRowItem(
            title = MembersCopy.inviteLinkTitle(link),
            description = MembersCopy.inviteLinkDetail(link, now),
            first = index == 0,
            trailing = {
                Button(
                    label = "Copy link",
                    variant = ButtonVariant.Secondary,
                    size = ButtonSize.Sm,
                    enabled = link.url != null,
                    onClick = { link.url?.let(onCopyLink) },
                )
                Button(
                    label = "Revoke",
                    variant = ButtonVariant.Ghost,
                    size = ButtonSize.Sm,
                    onClick = { dispatch(MembersSettingsContract.Intent.RevokeInviteLink(link.id)) },
                )
            },
        )
    }
    Row(modifier = Modifier.padding(top = Spacing.xs)) {
        Button(
            label = "Create link",
            variant = ButtonVariant.Ghost,
            size = ButtonSize.Sm,
            onClick = { dispatch(MembersSettingsContract.Intent.OpenInviteLinks) },
        )
    }
}
